/**
 * 状态验证工具
 * 用于验证PTT状态的一致性和有效性
 */

import { PttErrorType } from '../types/ptt';

// PTT状态验证规则
export interface PttStateValidationRule {
  name: string;
  validate: (state: any) => boolean;
  errorMessage: string;
  errorType: PttErrorType;
}

// 状态快照，用于回滚
export interface StateSnapshot {
  timestamp: number;
  pttState: {
    isActive: boolean;
    lastCommand: string;
    error: string | null;
  };
  deviceState: {
    selectedDeviceId: string | null;
    isConnected: boolean;
  };
}

/**
 * PTT状态验证器
 */
export class PttStateValidator {
  private static validationRules: PttStateValidationRule[] = [
    {
      name: 'device_required_for_ptt',
      validate: (state: any) => {
        // 如果PTT激活，必须有选中的设备
        if (state.ptt.isActive) {
          return state.devices.selectedDevice !== null;
        }
        return true;
      },
      errorMessage: 'PTT激活状态需要选中设备',
      errorType: PttErrorType.DEVICE_NOT_FOUND
    },
    {
      name: 'device_connection_required',
      validate: (state: any) => {
        // 如果PTT激活，设备必须连接
        if (state.ptt.isActive) {
          return state.devices.isConnected;
        }
        return true;
      },
      errorMessage: 'PTT激活状态需要设备连接',
      errorType: PttErrorType.DEVICE_OFFLINE
    },
    {
      name: 'command_consistency',
      validate: (state: any) => {
        // PTT状态和命令必须一致
        const { isActive, lastCommand } = state.ptt;
        if (isActive && lastCommand !== 'start_session') {
          return false;
        }
        if (!isActive && lastCommand === 'start_session') {
          return false;
        }
        return true;
      },
      errorMessage: 'PTT状态与命令不一致',
      errorType: PttErrorType.INVALID_COMMAND
    },
    {
      name: 'no_error_when_active',
      validate: (state: any) => {
        // PTT激活时不应该有错误
        if (state.ptt.isActive && state.ptt.error) {
          return false;
        }
        return true;
      },
      errorMessage: 'PTT激活状态不应有错误',
      errorType: PttErrorType.UNKNOWN_ERROR
    }
  ];

  /**
   * 验证PTT状态
   */
  static validatePttState(state: any): { isValid: boolean; errors: Array<{ rule: string; message: string; type: PttErrorType }> } {
    const errors: Array<{ rule: string; message: string; type: PttErrorType }> = [];

    for (const rule of this.validationRules) {
      if (!rule.validate(state)) {
        errors.push({
          rule: rule.name,
          message: rule.errorMessage,
          type: rule.errorType
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * 创建状态快照
   */
  static createSnapshot(state: any): StateSnapshot {
    return {
      timestamp: Date.now(),
      pttState: {
        isActive: state.ptt.isActive,
        lastCommand: state.ptt.lastCommand,
        error: state.ptt.error
      },
      deviceState: {
        selectedDeviceId: state.devices.selectedDevice?.getInfo()?.id || null,
        isConnected: state.devices.isConnected
      }
    };
  }

  /**
   * 应用状态快照
   */
  static applySnapshot(snapshot: StateSnapshot, setState: (updater: (state: any) => any) => void): void {
    setState((state: any) => ({
      ...state,
      ptt: {
        ...state.ptt,
        isActive: snapshot.pttState.isActive,
        lastCommand: snapshot.pttState.lastCommand,
        error: snapshot.pttState.error
      }
    }));
  }

  /**
   * 检查状态是否发生变化
   */
  static hasStateChanged(currentState: any, previousSnapshot: StateSnapshot): boolean {
    return (
      currentState.ptt.isActive !== previousSnapshot.pttState.isActive ||
      currentState.ptt.lastCommand !== previousSnapshot.pttState.lastCommand ||
      currentState.ptt.error !== previousSnapshot.pttState.error ||
      (currentState.devices.selectedDevice?.getInfo()?.id || null) !== previousSnapshot.deviceState.selectedDeviceId ||
      currentState.devices.isConnected !== previousSnapshot.deviceState.isConnected
    );
  }
}
